import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit2, Trash2, Truck, Package, MapPin, Phone, Mail, IndianRupee, CheckCircle, XCircle, Building2, Users } from 'lucide-react';
import { logisticsAPI, locationsAPI } from '../../services/api';

const LOGISTICS_TYPES = [
  { value: 'own', label: 'Own Logistics', color: 'bg-blue-100 text-blue-700' },
  { value: 'third_party', label: 'Third Party', color: 'bg-orange-100 text-orange-700' }
];

const SERVICE_TYPES = [
  { value: 'courier', label: 'Courier' },
  { value: 'transport', label: 'Transport' },
  { value: 'last_mile', label: 'Last Mile' },
  { value: 'express', label: 'Express' },
  { value: 'standard', label: 'Standard' }
];

const RATE_TYPES = [
  { value: 'per_kg', label: 'Per KG' },
  { value: 'flat_rate', label: 'Flat Rate' },
  { value: 'slab_based', label: 'Slab Based' }
];

export default function LogisticsSettings() {
  const { toast } = useToast();
  const [partners, setPartners] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    logistics_type: 'third_party',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    service_types: [],
    coverage_states: [],
    rate_type: 'per_kg',
    rate_per_kg: 0,
    flat_rate: 0,
    min_weight: 0.5,
    cod_available: false,
    cod_charges_percent: 0,
    api_integrated: false,
    tracking_url_template: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    loadPartners();
    loadStates();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await logisticsAPI.list();
      setPartners(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load logistics partners', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async () => {
    try {
      const data = await locationsAPI.getStatesDropdown();
      setStates(data);
    } catch (error) {
      console.error('Failed to load states:', error);
    }
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name || '',
        code: partner.code || '',
        logistics_type: partner.logistics_type || 'third_party',
        contact_person: partner.contact_person || '',
        contact_phone: partner.contact_phone || '',
        contact_email: partner.contact_email || '',
        service_types: partner.service_types || [],
        coverage_states: partner.coverage_states || [],
        rate_type: partner.rate_type || 'per_kg',
        rate_per_kg: partner.rate_per_kg || 0,
        flat_rate: partner.flat_rate || 0,
        min_weight: partner.min_weight || 0.5,
        cod_available: partner.cod_available || false,
        cod_charges_percent: partner.cod_charges_percent || 0,
        api_integrated: partner.api_integrated || false,
        tracking_url_template: partner.tracking_url_template || '',
        notes: partner.notes || '',
        is_active: partner.is_active !== false
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        code: '',
        logistics_type: 'third_party',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        service_types: [],
        coverage_states: [],
        rate_type: 'per_kg',
        rate_per_kg: 0,
        flat_rate: 0,
        min_weight: 0.5,
        cod_available: false,
        cod_charges_percent: 0,
        api_integrated: false,
        tracking_url_template: '',
        notes: '',
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast({ title: 'Error', description: 'Name and Code are required', variant: 'destructive' });
      return;
    }

    try {
      if (editingPartner) {
        await logisticsAPI.update(editingPartner.id, formData);
        toast({ title: 'Success', description: 'Logistics partner updated' });
      } else {
        await logisticsAPI.create(formData);
        toast({ title: 'Success', description: 'Logistics partner created' });
      }
      setShowDialog(false);
      loadPartners();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (partner) => {
    if (!window.confirm(`Are you sure you want to deactivate ${partner.name}?`)) return;
    try {
      await logisticsAPI.delete(partner.id);
      toast({ title: 'Success', description: 'Logistics partner deactivated' });
      loadPartners();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to deactivate partner', variant: 'destructive' });
    }
  };

  const toggleServiceType = (type) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(type)
        ? prev.service_types.filter(t => t !== type)
        : [...prev.service_types, type]
    }));
  };

  const toggleState = (stateId) => {
    setFormData(prev => ({
      ...prev,
      coverage_states: prev.coverage_states.includes(stateId)
        ? prev.coverage_states.filter(s => s !== stateId)
        : [...prev.coverage_states, stateId]
    }));
  };

  const filteredPartners = partners.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="logistics-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Logistics Partners</h1>
          <p className="text-gray-500">Manage delivery and logistics partners for E-Commerce</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-partner-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Partner
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search partners..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-partners"
        />
      </div>

      {/* Partners Grid */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : filteredPartners.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No logistics partners found</p>
            <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" /> Add First Partner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPartners.map((partner) => {
            const logisticsTypeConfig = LOGISTICS_TYPES.find(t => t.value === partner.logistics_type) || LOGISTICS_TYPES[1];
            return (
            <Card key={partner.id} className={`${!partner.is_active ? 'opacity-60' : ''}`} data-testid={`partner-card-${partner.code}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${partner.logistics_type === 'own' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                      {partner.logistics_type === 'own' ? (
                        <Building2 className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Truck className="w-5 h-5 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base">{partner.name}</CardTitle>
                      <p className="text-sm text-gray-500">{partner.code}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(partner)} data-testid={`edit-${partner.code}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(partner)} data-testid={`delete-${partner.code}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Logistics Type Badge */}
                <Badge className={logisticsTypeConfig.color}>{logisticsTypeConfig.label}</Badge>
                
                {/* Contact Info */}
                {partner.contact_person && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-3 h-3" />
                    <span>{partner.contact_person}</span>
                    {partner.contact_phone && <span>• {partner.contact_phone}</span>}
                  </div>
                )}
                
                {/* Service Types */}
                <div className="flex flex-wrap gap-1">
                  {partner.service_types?.map(type => (
                    <Badge key={type} variant="secondary" className="text-xs">
                      {type.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>

                {/* Rates */}
                <div className="flex items-center gap-2 text-sm">
                  <IndianRupee className="w-3 h-3 text-green-600" />
                  {partner.rate_type === 'per_kg' ? (
                    <span>₹{partner.rate_per_kg}/kg (min {partner.min_weight}kg)</span>
                  ) : partner.rate_type === 'flat_rate' ? (
                    <span>₹{partner.flat_rate} flat</span>
                  ) : (
                    <span>Slab based pricing</span>
                  )}
                </div>

                {/* COD & Coverage */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    {partner.cod_available ? (
                      <Badge className="bg-green-100 text-green-700">COD Available</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">No COD</Badge>
                    )}
                  </div>
                  {partner.coverage_state_names?.length > 0 && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <MapPin className="w-3 h-3" />
                      <span>{partner.coverage_state_names.length} states</span>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Badge variant={partner.is_active ? 'default' : 'secondary'}>
                    {partner.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {partner.api_integrated && (
                    <Badge className="bg-blue-100 text-blue-700">API Integrated</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )})}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Logistics Partner'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Logistics Type */}
            <div className="space-y-2">
              <Label>Logistics Type *</Label>
              <Select value={formData.logistics_type} onValueChange={(v) => setFormData({ ...formData, logistics_type: v })}>
                <SelectTrigger data-testid="logistics-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOGISTICS_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Partner Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Blue Dart Express"
                  data-testid="partner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., BDART"
                  data-testid="partner-code-input"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="Email"
                  type="email"
                />
              </div>
            </div>

            {/* Service Types */}
            <div className="space-y-2">
              <Label>Service Types</Label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_TYPES.map((type) => (
                  <div key={type.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.service_types.includes(type.value)}
                      onCheckedChange={() => toggleServiceType(type.value)}
                    />
                    <span className="text-sm">{type.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rates */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Rate Type</Label>
                <Select value={formData.rate_type} onValueChange={(v) => setFormData({ ...formData, rate_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rate per KG (₹)</Label>
                <Input
                  type="number"
                  value={formData.rate_per_kg}
                  onChange={(e) => setFormData({ ...formData, rate_per_kg: parseFloat(e.target.value) || 0 })}
                  disabled={formData.rate_type !== 'per_kg'}
                />
              </div>
              <div className="space-y-2">
                <Label>Flat Rate (₹)</Label>
                <Input
                  type="number"
                  value={formData.flat_rate}
                  onChange={(e) => setFormData({ ...formData, flat_rate: parseFloat(e.target.value) || 0 })}
                  disabled={formData.rate_type !== 'flat_rate'}
                />
              </div>
              <div className="space-y-2">
                <Label>Min Weight (KG)</Label>
                <Input
                  type="number"
                  value={formData.min_weight}
                  onChange={(e) => setFormData({ ...formData, min_weight: parseFloat(e.target.value) || 0.5 })}
                  step="0.1"
                />
              </div>
            </div>

            {/* COD Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.cod_available}
                  onCheckedChange={(v) => setFormData({ ...formData, cod_available: v })}
                />
                <Label>COD Available</Label>
              </div>
              {formData.cod_available && (
                <div className="space-y-2">
                  <Label>COD Charges (%)</Label>
                  <Input
                    type="number"
                    value={formData.cod_charges_percent}
                    onChange={(e) => setFormData({ ...formData, cod_charges_percent: parseFloat(e.target.value) || 0 })}
                    step="0.1"
                  />
                </div>
              )}
            </div>

            {/* Coverage States */}
            <div className="space-y-2">
              <Label>Coverage States ({formData.coverage_states.length} selected)</Label>
              <div className="max-h-40 overflow-y-auto border rounded p-3 grid grid-cols-3 gap-2">
                {states.map((state) => (
                  <div key={state.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.coverage_states.includes(state.id)}
                      onCheckedChange={() => toggleState(state.id)}
                    />
                    <span className="text-sm truncate">{state.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* API Integration */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.api_integrated}
                  onCheckedChange={(v) => setFormData({ ...formData, api_integrated: v })}
                />
                <Label>API Integrated</Label>
              </div>
              {formData.api_integrated && (
                <div className="space-y-2">
                  <Label>Tracking URL Template</Label>
                  <Input
                    value={formData.tracking_url_template}
                    onChange={(e) => setFormData({ ...formData, tracking_url_template: e.target.value })}
                    placeholder="https://track.partner.com/{tracking_no}"
                  />
                  <p className="text-xs text-gray-500">Use {'{tracking_no}'} as placeholder for AWB number</p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this partner..."
                rows={2}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Active Partner</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-partner-btn">
              {editingPartner ? 'Update Partner' : 'Add Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
